'use client'

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useLocale } from 'next-intl'
import { Link, useRouter } from '@/i18n/navigation'
import { t } from '@/i18n/translations'
import { fetchStocksByExchange, type IndexQuote } from '@/services/marketApi'
import BrokerRecommendations from '@/components/BrokerRecommendations'
import { usBrokers } from '@/data/indexData'

/* Types */

type MarketTab = 'us' | 'eu' | 'americas' | 'asia' | 'mea'

interface SubFilter {
  key: string
  label: string
  exchanges: string[]
  flag?: string
  detailPrefix: string
}

type SortKey = 'symbol' | 'price' | 'changePercent' | 'monthly' | 'yearly' | 'volume'
type SortDir = 'asc' | 'desc'

/* Market Config */

const marketConfig: Record<MarketTab, SubFilter[]> = {
  us: [
    { key: 'all-us', label: 'stocks.all', exchanges: ['XNAS', 'XNYS'], detailPrefix: 'us-stock' },
    { key: 'xnas', label: 'NASDAQ', exchanges: ['XNAS'], detailPrefix: 'us-stock' },
    { key: 'xnys', label: 'NYSE', exchanges: ['XNYS'], detailPrefix: 'us-stock' },
  ],
  eu: [
    { key: 'all-eu', label: 'stocks.all', exchanges: ['XLON', 'XFRA', 'XETR', 'XSTU', 'XPAR', 'XMAD', 'XSWX'], detailPrefix: 'eu-stock' },
    { key: 'xlon', label: 'London', exchanges: ['XLON'], flag: 'gb', detailPrefix: 'eu-stock' },
    { key: 'xfra', label: 'Frankfurt', exchanges: ['XFRA', 'XETR'], flag: 'de', detailPrefix: 'eu-stock' },
    { key: 'xstu', label: 'Stuttgart', exchanges: ['XSTU'], flag: 'de', detailPrefix: 'eu-stock' },
    { key: 'xpar', label: 'Paris', exchanges: ['XPAR'], flag: 'fr', detailPrefix: 'eu-stock' },
    { key: 'xmad', label: 'Madrid', exchanges: ['XMAD'], flag: 'es', detailPrefix: 'eu-stock' },
    { key: 'xswx', label: 'Zurich', exchanges: ['XSWX'], flag: 'ch', detailPrefix: 'eu-stock' },
  ],
  americas: [
    { key: 'all-americas', label: 'stocks.all', exchanges: ['XTSE', 'BVMF'], detailPrefix: 'stock' },
    { key: 'xtse', label: 'stocks.canada', exchanges: ['XTSE'], flag: 'ca', detailPrefix: 'stock' },
    { key: 'bvmf', label: 'stocks.brazil', exchanges: ['BVMF'], flag: 'br', detailPrefix: 'stock' },
  ],
  asia: [
    { key: 'all-asia', label: 'stocks.all', exchanges: ['XBOM', 'XNSE', 'XKRX', 'XSHG', 'XSHE', 'XTAI', 'XIDX', 'XASX'], detailPrefix: 'in-stock' },
    { key: 'xbom', label: 'stocks.india', exchanges: ['XBOM', 'XNSE'], flag: 'in', detailPrefix: 'in-stock' },
    { key: 'xkrx', label: 'stocks.southKorea', exchanges: ['XKRX'], flag: 'kr', detailPrefix: 'kr-stock' },
    { key: 'xshg', label: 'stocks.china', exchanges: ['XSHG', 'XSHE'], flag: 'cn', detailPrefix: 'hk-stock' },
    { key: 'xtai', label: 'stocks.taiwan', exchanges: ['XTAI'], flag: 'tw', detailPrefix: 'stock' },
    { key: 'xidx', label: 'stocks.indonesia', exchanges: ['XIDX'], flag: 'id', detailPrefix: 'stock' },
    { key: 'xasx', label: 'stocks.australia', exchanges: ['XASX'], flag: 'au', detailPrefix: 'stock' },
  ],
  mea: [
    { key: 'all-mea', label: 'stocks.all', exchanges: ['XSAU'], detailPrefix: 'stock' },
    { key: 'xsau', label: 'stocks.saudiArabia', exchanges: ['XSAU'], flag: 'sa', detailPrefix: 'stock' },
  ],
}

const marketTabDefs: { key: MarketTab; i18n: string; flagCode: string }[] = [
  { key: 'us', i18n: 'cat.us', flagCode: 'us' },
  { key: 'eu', i18n: 'cat.eu', flagCode: 'eu' },
  { key: 'americas', i18n: 'stocks.americas', flagCode: 'br' },
  { key: 'asia', i18n: 'stocks.asia', flagCode: 'jp' },
  { key: 'mea', i18n: 'stocks.mea', flagCode: 'sa' },
]

const FLAG_CDN = 'https://flagcdn.com/w40'
const PAGE_SIZE = 30

/* Format Helpers */

function fmtVal(val: number): string {
  const [int, dec] = val.toFixed(2).split('.')
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec
}

function fmtVol(vol: number): string {
  if (vol >= 1_000_000_000) return (vol / 1_000_000_000).toFixed(2).replace('.', ',') + ' Mrd'
  if (vol >= 1_000_000) return (vol / 1_000_000).toFixed(2).replace('.', ',') + ' Mln'
  if (vol >= 1_000) return (vol / 1_000).toFixed(1).replace('.', ',') + ' B'
  return vol.toLocaleString('tr-TR')
}

function Skeleton({ w, h = 16, className = '' }: { w: number; h?: number; className?: string }) {
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{ width: w, height: h, backgroundColor: 'var(--border-color)' }}
    />
  )
}

/* Page */

export default function StocksClient() {
  const lang = useLocale()
  const router = useRouter()

  const [marketTab, setMarketTab] = useState<MarketTab>('us')
  const [subFilterKey, setSubFilterKey] = useState('all-us')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [stocks, setStocks] = useState<IndexQuote[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [sortKey, setSortKey] = useState<SortKey>('changePercent')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const subFilters = marketConfig[marketTab]
  const activeFilter = subFilters.find(f => f.key === subFilterKey) || subFilters[0]

  // When market tab changes, reset to first sub-filter
  useEffect(() => {
    const first = marketConfig[marketTab][0]
    setSubFilterKey(first.key)
    setSearchQuery('')
    setDebouncedSearch('')
    setPage(1)
  }, [marketTab])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setPage(1)
    }, 350)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch data
  const loadStocks = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchStocksByExchange(activeFilter.exchanges, page, PAGE_SIZE, debouncedSearch || undefined)
      setStocks(res.items)
      setTotalCount(res.totalCount)
    } catch {
      setStocks([])
      setTotalCount(0)
    }
    setLoading(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter.exchanges.join(','), page, debouncedSearch])

  useEffect(() => {
    loadStocks()
  }, [loadStocks])

  // Client-side sort on current page
  const sorted = useMemo(() => {
    return [...stocks].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'symbol': cmp = a.symbol.localeCompare(b.symbol); break
        case 'price': cmp = a.close - b.close; break
        case 'changePercent': cmp = a.changePercent - b.changePercent; break
        case 'monthly': cmp = (a.monthly ?? -Infinity) - (b.monthly ?? -Infinity); break
        case 'yearly': cmp = (a.yearly ?? -Infinity) - (b.yearly ?? -Infinity); break
        case 'volume': cmp = (a.volume ?? 0) - (b.volume ?? 0); break
      }
      return sortDir === 'desc' ? -cmp : cmp
    })
  }, [stocks, sortKey, sortDir])

  function handleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  // Summary cards from current page data
  const topGainers = useMemo(() =>
    [...stocks].filter(s => s.changePercent > 0).sort((a, b) => b.changePercent - a.changePercent).slice(0, 3)
  , [stocks])

  const topLosers = useMemo(() =>
    [...stocks].filter(s => s.changePercent < 0).sort((a, b) => a.changePercent - b.changePercent).slice(0, 3)
  , [stocks])

  const mostActive = useMemo(() =>
    [...stocks].sort((a, b) => (b.volume ?? 0) - (a.volume ?? 0)).slice(0, 3)
  , [stocks])

  const navigateToDetail = (symbol: string) => {
    const prefix = activeFilter.detailPrefix
    if (prefix === 'us-stock') {
      router.push({ pathname: '/us-stock/[symbol]', params: { symbol } })
    } else if (prefix === 'eu-stock') {
      router.push({ pathname: '/eu-stock/[symbol]', params: { symbol } })
    } else if (prefix === 'jp-stock') {
      router.push({ pathname: '/jp-stock/[symbol]', params: { symbol } })
    } else if (prefix === 'hk-stock') {
      router.push({ pathname: '/hk-stock/[symbol]', params: { symbol } })
    } else if (prefix === 'kr-stock') {
      router.push({ pathname: '/kr-stock/[symbol]', params: { symbol } })
    } else if (prefix === 'in-stock') {
      router.push({ pathname: '/in-stock/[symbol]', params: { symbol } })
    } else {
      router.push({ pathname: '/stock/[symbol]', params: { symbol } })
    }
  }

  // Pagination
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const paginationPages = useMemo(() => {
    const pages: (number | 'dots')[] = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (page > 3) pages.push('dots')
      const start = Math.max(2, page - 1)
      const end = Math.min(totalPages - 1, page + 1)
      for (let i = start; i <= end; i++) pages.push(i)
      if (page < totalPages - 2) pages.push('dots')
      pages.push(totalPages)
    }
    return pages
  }, [totalPages, page])

  return (
    <main className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[13px] mb-6 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        <Link href="/" className="hover:underline transition-colors shrink-0" style={{ color: 'var(--accent-color)' }}>{t('nav.home', lang)}</Link>
        <span className="select-none shrink-0">/</span>
        <span className="font-semibold truncate" style={{ color: 'var(--text-main)' }}>{t('stocks.title', lang)}</span>
      </nav>

      {/* Heading */}
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight m-0" style={{ color: 'var(--text-main)' }}>
          {t('stocks.title', lang)}
        </h1>
        <p className="text-sm mt-1 m-0" style={{ color: 'var(--text-muted)' }}>
          {t('stocks.desc', lang)}
        </p>
      </div>

      {/* Market Tabs */}
      <div
        className="flex rounded-xl border mb-6 overflow-x-auto sm:overflow-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ borderColor: 'var(--border-color)' }}
      >
        {marketTabDefs.map(mt => (
          <button
            key={mt.key}
            onClick={() => setMarketTab(mt.key)}
            className="flex-shrink-0 sm:flex-1 py-3 px-4 sm:px-0 text-sm font-semibold transition-colors border-none cursor-pointer whitespace-nowrap"
            style={{
              backgroundColor: marketTab === mt.key ? 'var(--accent-color)' : 'var(--bg-card)',
              color: marketTab === mt.key ? '#fff' : 'var(--text-muted)',
            }}
          >
            <img src={`${FLAG_CDN}/${mt.flagCode}.png`} alt="" className="inline-block w-5 h-3.5 rounded-[2px] object-cover mr-1.5 align-middle" />{t(mt.i18n, lang)}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      {!loading && stocks.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {/* Top Gainers */}
          <div
            className="rounded-xl border p-4"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--chart-up)' }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                {t('stocks.topGainers', lang)}
              </span>
            </div>
            {topGainers.length === 0 ? (
              <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>&mdash;</div>
            ) : topGainers.map(s => (
              <div
                key={s.symbol}
                className="flex items-center justify-between py-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigateToDetail(s.symbol)}
              >
                <span className="text-[13px] font-semibold" style={{ color: 'var(--text-main)' }}>{s.symbol}</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--chart-up)' }}>
                  +%{Math.abs(s.changePercent).toFixed(2).replace('.', ',')} &uarr;
                </span>
              </div>
            ))}
          </div>

          {/* Top Losers */}
          <div
            className="rounded-xl border p-4"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--chart-down)' }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                {t('stocks.topLosers', lang)}
              </span>
            </div>
            {topLosers.length === 0 ? (
              <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>&mdash;</div>
            ) : topLosers.map(s => (
              <div
                key={s.symbol}
                className="flex items-center justify-between py-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigateToDetail(s.symbol)}
              >
                <span className="text-[13px] font-semibold" style={{ color: 'var(--text-main)' }}>{s.symbol}</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--chart-down)' }}>
                  -%{Math.abs(s.changePercent).toFixed(2).replace('.', ',')} &darr;
                </span>
              </div>
            ))}
          </div>

          {/* Most Active */}
          <div
            className="rounded-xl border p-4"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--accent-color)' }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                {t('stocks.mostActive', lang)}
              </span>
            </div>
            {mostActive.length === 0 ? (
              <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>&mdash;</div>
            ) : mostActive.map(s => (
              <div
                key={s.symbol}
                className="flex items-center justify-between py-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigateToDetail(s.symbol)}
              >
                <span className="text-[13px] font-semibold" style={{ color: 'var(--text-main)' }}>{s.symbol}</span>
                <span className="text-[13px] font-medium tabular-nums" style={{ color: 'var(--text-muted)' }}>
                  {s.volume != null ? fmtVol(s.volume) : '\u2014'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sub-filters + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-1 flex-wrap">
          {subFilters.map(f => {
            const isI18n = f.label.includes('.')
            return (
              <button
                key={f.key}
                onClick={() => { setSubFilterKey(f.key); setPage(1) }}
                className="px-3.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors border-none cursor-pointer"
                style={{
                  backgroundColor: subFilterKey === f.key ? 'var(--accent-color)' : 'transparent',
                  color: subFilterKey === f.key ? '#fff' : 'var(--text-muted)',
                }}
              >
                {f.flag && (
                  <img src={`${FLAG_CDN}/${f.flag}.png`} alt="" className="inline-block w-4 h-3 rounded-[2px] object-cover mr-1 align-middle" />
                )}
                {isI18n ? t(f.label, lang) : f.label}
              </button>
            )
          })}
        </div>

        {/* Search */}
        <div className="relative sm:ml-auto w-full sm:w-auto">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="var(--accent-color)" strokeWidth="2.2" strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder={t('stocks.searchPlaceholder', lang)}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="constituent-search pl-9 pr-3 py-2 rounded-lg text-[13px] outline-none w-full sm:w-72 transition-shadow"
            style={{
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text-main)',
              border: '1.5px solid var(--accent-color)',
              boxShadow: '0 0 0 3px color-mix(in srgb, var(--accent-color) 14%, transparent)',
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse" style={{ minWidth: 720 }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-body)' }}>
                {([
                  { key: 'symbol' as SortKey, label: t('th.stock', lang), align: 'left' as const, px: 'pl-5 pr-3' },
                  { key: 'price' as SortKey, label: t('th.price', lang), align: 'right' as const, px: 'px-3' },
                  { key: 'changePercent' as SortKey, label: t('th.change', lang), align: 'right' as const, px: 'px-3' },
                  { key: 'monthly' as SortKey, label: t('th.monthly', lang), align: 'right' as const, px: 'px-3' },
                  { key: 'yearly' as SortKey, label: t('th.yearly', lang), align: 'right' as const, px: 'px-3' },
                  { key: 'volume' as SortKey, label: t('th.volume', lang), align: 'right' as const, px: 'pr-5 pl-3' },
                ] as const).map(h => (
                  <th scope="col"
                    key={h.key}
                    onClick={() => handleSort(h.key)}
                    className={`py-2.5 text-[10px] font-bold uppercase tracking-widest ${h.px} cursor-pointer select-none transition-colors hover:opacity-80`}
                    style={{
                      color: sortKey === h.key ? 'var(--accent-color)' : 'var(--text-muted)',
                      borderBottom: '2px solid var(--border-color)',
                      textAlign: h.align,
                    }}
                  >
                    {h.label}
                    <span className="ml-1 text-[9px]">
                      {sortKey === h.key ? (sortDir === 'asc' ? '\u2191' : '\u2193') : '\u2195'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: PAGE_SIZE > 10 ? 10 : PAGE_SIZE }).map((_, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td className="pl-5 pr-3 py-3"><Skeleton w={100} /></td>
                      <td className="px-3 py-3 text-right"><Skeleton w={60} className="ml-auto" /></td>
                      <td className="px-3 py-3 text-right"><Skeleton w={56} className="ml-auto" /></td>
                      <td className="px-3 py-3 text-right"><Skeleton w={50} className="ml-auto" /></td>
                      <td className="px-3 py-3 text-right"><Skeleton w={50} className="ml-auto" /></td>
                      <td className="pr-5 pl-3 py-3 text-right"><Skeleton w={70} className="ml-auto" /></td>
                    </tr>
                  ))
                : sorted.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-[13px] font-medium" style={{ color: 'var(--text-muted)' }}>
                        {debouncedSearch ? t('status.noResults', lang) : t('status.loadingData', lang)}
                      </td>
                    </tr>
                  )
                : sorted.map((s, i) => {
                    const up = s.changePercent >= 0
                    const c = up ? 'var(--chart-up)' : 'var(--chart-down)'
                    return (
                      <tr
                        key={s.symbol}
                        className="cursor-pointer transition-colors duration-100 hover:bg-[var(--bg-body)]"
                        style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--border-color)' : 'none' }}
                        onClick={() => navigateToDetail(s.symbol)}
                      >
                        <td className="pl-5 pr-3 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0"
                              style={{ backgroundColor: up ? 'rgba(76,175,80,0.1)' : 'rgba(229,57,53,0.1)', color: c }}
                            >
                              {s.symbol.slice(0, 2)}
                            </div>
                            <div>
                              <div className="text-[13px] font-bold leading-tight" style={{ color: 'var(--text-main)' }}>{s.symbol}</div>
                              <div className="text-[10px] mt-0.5 leading-tight truncate max-w-[160px]" style={{ color: 'var(--text-muted)' }}>{s.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--text-main)' }}>{fmtVal(s.close)}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums"
                            style={{ backgroundColor: up ? 'rgba(76,175,80,0.12)' : 'rgba(229,57,53,0.12)', color: c }}
                          >
                            {up ? '+' : ''}%{Math.abs(s.changePercent).toFixed(2).replace('.', ',')}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {s.monthly != null ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium tabular-nums"
                              style={{ backgroundColor: s.monthly >= 0 ? 'rgba(76,175,80,0.08)' : 'rgba(229,57,53,0.08)', color: s.monthly >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}
                            >
                              {s.monthly >= 0 ? '+' : ''}%{Math.abs(s.monthly).toFixed(2).replace('.', ',')}
                            </span>
                          ) : <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {s.yearly != null ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium tabular-nums"
                              style={{ backgroundColor: s.yearly >= 0 ? 'rgba(76,175,80,0.08)' : 'rgba(229,57,53,0.08)', color: s.yearly >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}
                            >
                              {s.yearly >= 0 ? '+' : ''}%{Math.abs(s.yearly).toFixed(2).replace('.', ',')}
                            </span>
                          ) : <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>}
                        </td>
                        <td className="pr-5 pl-3 py-2.5 text-right">
                          <span className="text-[12px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
                            {s.volume != null ? fmtVol(s.volume) : '\u2014'}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <Skeleton w={120} h={16} className="mb-2" />
                  <Skeleton w={200} h={12} />
                </div>
              ))
            : sorted.length === 0 ? (
                <div className="py-8 text-center">
                  <span className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
                    {debouncedSearch ? t('status.noResults', lang) : t('status.loadingData', lang)}
                  </span>
                </div>
              )
            : sorted.map((s, i) => {
                const up = s.changePercent >= 0
                const c = up ? 'var(--chart-up)' : 'var(--chart-down)'
                return (
                  <div
                    key={s.symbol}
                    className="px-4 py-3 cursor-pointer transition-colors active:opacity-80"
                    style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--border-color)' : 'none' }}
                    onClick={() => navigateToDetail(s.symbol)}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0"
                          style={{ backgroundColor: up ? 'rgba(76,175,80,0.1)' : 'rgba(229,57,53,0.1)', color: c }}
                        >
                          {s.symbol.slice(0, 2)}
                        </div>
                        <div>
                          <span className="text-[13px] font-bold" style={{ color: 'var(--text-main)' }}>{s.symbol}</span>
                          <div className="text-[10px] leading-tight truncate max-w-[140px]" style={{ color: 'var(--text-muted)' }}>{s.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[14px] font-bold tabular-nums" style={{ color: 'var(--text-main)' }}>{fmtVal(s.close)}</div>
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold tabular-nums"
                          style={{ backgroundColor: up ? 'rgba(76,175,80,0.12)' : 'rgba(229,57,53,0.12)', color: c }}
                        >
                          {up ? '+' : ''}%{Math.abs(s.changePercent).toFixed(2).replace('.', ',')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {s.monthly != null && (
                        <span className="text-[10px] tabular-nums" style={{ color: s.monthly >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}>
                          {lang === 'en' ? 'M' : 'Ay'}: {s.monthly >= 0 ? '+' : ''}%{Math.abs(s.monthly).toFixed(1).replace('.', ',')}
                        </span>
                      )}
                      {s.yearly != null && (
                        <span className="text-[10px] tabular-nums" style={{ color: s.yearly >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}>
                          {lang === 'en' ? 'Y' : 'Yıl'}: {s.yearly >= 0 ? '+' : ''}%{Math.abs(s.yearly).toFixed(1).replace('.', ',')}
                        </span>
                      )}
                      {s.volume != null && s.volume > 0 && (
                        <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
                          Vol: {fmtVol(s.volume)}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
        </div>

        {/* No search results */}
        {!loading && debouncedSearch && stocks.length === 0 && (
          <div className="py-8 text-center">
            <span className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
              {t('status.noResults', lang)}
            </span>
          </div>
        )}

        {/* Numbered Pagination */}
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 sm:px-5 py-3" style={{ borderTop: '1px solid var(--border-color)' }}>
            <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
              {((page - 1) * PAGE_SIZE) + 1}&ndash;{Math.min(page * PAGE_SIZE, totalCount)} / {totalCount}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-[13px] font-medium transition-colors disabled:opacity-30 border-none cursor-pointer"
                style={{ color: 'var(--text-main)', backgroundColor: 'transparent' }}
              >
                &lsaquo;
              </button>
              {paginationPages.map((p, idx) =>
                p === 'dots' ? (
                  <span key={`dots-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px]" style={{ color: 'var(--text-muted)' }}>&hellip;</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-[12px] font-semibold transition-colors border-none cursor-pointer"
                    style={{
                      backgroundColor: page === p ? 'var(--accent-color)' : 'transparent',
                      color: page === p ? '#fff' : 'var(--text-muted)',
                    }}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-[13px] font-medium transition-colors disabled:opacity-30 border-none cursor-pointer"
                style={{ color: 'var(--text-main)', backgroundColor: 'transparent' }}
              >
                &rsaquo;
              </button>
            </div>
          </div>
        )}

        {/* Result count */}
        {!loading && stocks.length > 0 && totalCount <= PAGE_SIZE && (
          <div className="px-6 py-3 border-t text-[11px]" style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
            {totalCount} {t('stocks.listed', lang)}
          </div>
        )}
      </div>

      {/* Brokers CTA — kullanıcı hisse görüyor ama nasıl alacağını bilmiyor */}
      <div className="mt-6">
        <BrokerRecommendations
          brokers={usBrokers}
          source="stocks_list"
          customHeadline={lang === 'en'
            ? '💸 Ready to buy your first stock?'
            : '💸 İlk Hisseni Almaya Hazır mısın?'}
          customSubline={lang === 'en'
            ? 'You need a brokerage account to invest in any of these stocks. Compare 30+ trusted brokers in seconds — zero commission options available.'
            : 'Bu hisselere yatırım yapmak için bir aracı kuruma ihtiyacın var. 30+ güvenilir broker’ı saniyeler içinde karşılaştır — sıfır komisyon seçenekleri mevcut.'}
          customCta={lang === 'en' ? 'Open an account →' : 'Hesap Aç →'}
        />
      </div>
    </main>
  )
}
